/**
 * Polish readings of the provider's vocabulary (spec 013).
 *
 * The lot payload carries Copart's and IAAI's own words — "Engine start
 * program", "Non-insurance Company", "CERT OF TITLE SLVG REBUILDABLE" — and a
 * Polish visitor deciding whether to bid needs them in Polish. Every list here
 * is CLOSED in practice (sampled across both platforms, 2026-09-07: 34 damage
 * types, 8 run conditions, 4 seller types, 54 document names that reduce to a
 * dozen patterns), so a dictionary is safe — and every lookup falls back to the
 * raw value, so an unmapped word is shown as the house wrote it rather than
 * hidden (FR-011, Principle I).
 *
 * Where the original carries legal weight (document type), the Polish reading
 * is a gloss and the raw value is returned alongside so the UI can show both.
 */

export type TReading = {
  /** What the visitor reads. Equals `raw` when there is no mapping. */
  label: string;
  /** The provider's wording, when the label is a translation of it. */
  raw: string | null;
};

const key = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');

const lookup = (map: Record<string, string>, value: string | null): TReading | null => {
  if (value === null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const label = map[key(trimmed)];
  return label ? { label, raw: trimmed } : { label: trimmed, raw: null };
};

/** Primary and secondary damage share one vocabulary (the case differs by source). */
const DAMAGE: Record<string, string> = {
  'front end': 'Przód',
  'rear end': 'Tył',
  rear: 'Tył',
  front: 'Przód',
  side: 'Bok',
  'left side': 'Lewy bok',
  'right side': 'Prawy bok',
  'left & right side': 'Oba boki',
  'left front': 'Lewy przód',
  'right front': 'Prawy przód',
  'left rear': 'Lewy tył',
  'right rear': 'Prawy tył',
  'front & rear': 'Przód i tył',
  'all over': 'Cały pojazd',
  'top/roof': 'Dach',
  roof: 'Dach',
  undercarriage: 'Podwozie',
  'minor dent/scratches': 'Wgniecenia / rysy',
  'normal wear & tear': 'Normalne zużycie',
  'normal wear': 'Normalne zużycie',
  mechanical: 'Mechaniczne',
  'engine damage': 'Silnik',
  'engine burn': 'Spalony silnik',
  'burn - engine': 'Spalony silnik',
  'transmission damage': 'Skrzynia biegów',
  suspension: 'Zawieszenie',
  electrical: 'Instalacja elektryczna',
  'frame damage': 'Rama / płyta podłogowa',
  'water/flood': 'Zalanie',
  flood: 'Zalanie',
  hail: 'Grad',
  burn: 'Pożar',
  'total burn': 'Spalony całkowicie',
  vandalism: 'Wandalizm',
  theft: 'Kradzież',
  stripped: 'Rozkradziony',
  strip: 'Rozkradziony',
  repossession: 'Windykacja',
  biohazard: 'Zagrożenie biologiczne',
  'missing/altered vin': 'Brak / zmieniony VIN',
  unknown: 'Nieznane',
};

const RUN_CONDITION: Record<string, string> = {
  'runs and drives': 'Odpala i jeździ',
  'run & drive verified': 'Odpala i jeździ — potwierdzone',
  'run and drive': 'Odpala i jeździ',
  'vehicle starts': 'Odpala',
  'engine start program': 'Odpala silnik',
  'enhanced vehicles': 'Enhanced — lżejsze szkody',
  'stationary / no information': 'Brak informacji',
  stationary: 'Nie uruchomiono',
  default: 'Brak informacji',
  unknown: 'Brak informacji',
};

/**
 * The distinction a buyer cares about is insurer versus trade. The provider's
 * `non_insurance` is everyone who is not an insurer — dealers, rental fleets,
 * companies — and is read as trade; `dealer` and `finance` are named when the
 * house names them.
 */
const SELLER_TYPE: Record<string, string> = {
  insurance: 'Ubezpieczyciel',
  'insurance company': 'Ubezpieczyciel',
  non_insurance: 'Dealer / firma',
  'non-insurance company': 'Dealer / firma',
  dealer: 'Dealer',
  finance: 'Bank / leasing',
  unknown: 'Nieznany',
};

const LOSS_TYPE: Record<string, string> = {
  collision: 'Kolizja',
  theft: 'Kradzież',
  flood: 'Zalanie',
  water: 'Zalanie',
  hail: 'Grad',
  fire: 'Pożar',
  vandalism: 'Wandalizm',
  mechanical: 'Awaria mechaniczna',
  other: 'Inna',
  unknown: 'Nieznana',
};

const COLOR: Record<string, string> = {
  white: 'Biały',
  black: 'Czarny',
  silver: 'Srebrny',
  gray: 'Szary',
  grey: 'Szary',
  charcoal: 'Grafitowy',
  pewter: 'Cynowy',
  blue: 'Niebieski',
  'dark blue': 'Granatowy',
  'light blue': 'Jasnoniebieski',
  red: 'Czerwony',
  maroon: 'Bordowy',
  burgundy: 'Bordowy',
  green: 'Zielony',
  yellow: 'Żółty',
  gold: 'Złoty',
  orange: 'Pomarańczowy',
  tan: 'Beżowy',
  beige: 'Beżowy',
  brown: 'Brązowy',
  purple: 'Fioletowy',
  'two tone': 'Dwukolorowy',
  burn: 'Spalony',
  unknown: 'Nieznany',
};

const FUEL: Record<string, string> = {
  gasoline: 'Benzyna',
  gas: 'Benzyna',
  diesel: 'Diesel',
  hybrid: 'Hybryda',
  'hybrid engine': 'Hybryda',
  'electric and gas hybrid': 'Hybryda',
  electric: 'Elektryczny',
  flexiblefuel: 'Flex fuel (benzyna / E85)',
  flexible: 'Flex fuel (benzyna / E85)',
  'flexible fuel': 'Flex fuel (benzyna / E85)',
  other: 'Inne',
};

const TRANSMISSION: Record<string, string> = {
  automatic: 'Automatyczna',
  manual: 'Manualna',
  unknown: 'Nieznana',
};

const DRIVE: Record<string, string> = {
  'front wheel drive': 'Na przód (FWD)',
  'front-wheel drive': 'Na przód (FWD)',
  'rear wheel drive': 'Na tył (RWD)',
  'rear-wheel drive': 'Na tył (RWD)',
  'all wheel drive': 'Na cztery koła (AWD)',
  '4x4 drive': '4x4',
  'four by four': '4x4',
  '4x4 w/rear wheel drv': '4x4 (dołączany, baza RWD)',
  '4x4 w/front whl drv': '4x4 (dołączany, baza FWD)',
  '4x2 drive': '4x2',
};

const BODY_STYLE: Record<string, string> = {
  sedan: 'Sedan',
  'sedan/hatchback': 'Sedan / hatchback',
  hatchback: 'Hatchback',
  'hatchback/crossover': 'Hatchback / crossover',
  suv: 'SUV',
  'suv/crossover': 'SUV / crossover',
  'convertible/suv': 'Kabriolet / SUV',
  coupe: 'Coupé',
  convertible: 'Kabriolet',
  wagon: 'Kombi',
  minivan: 'Minivan',
  van: 'Van',
  truck: 'Pickup / ciężarowy',
  'commercial truck': 'Ciężarowy',
  'mid size sport': 'Sportowy',
};

const VEHICLE_TYPE: Record<string, string> = {
  automobile: 'Samochód osobowy',
  sedan: 'Samochód osobowy',
  coupe: 'Samochód osobowy',
  suv: 'SUV',
  pickup: 'Pickup',
  truck: 'Ciężarowy',
  'medium duty/box trucks': 'Dostawczy / średni ciężarowy',
  'heavy duty trucks': 'Ciężki ciężarowy',
  motorcycle: 'Motocykl',
  atv: 'Quad / ATV',
  bus: 'Autobus',
  'recreational vehicle (rv)': 'Kamper',
  'motor home': 'Kamper',
  trailers: 'Przyczepa',
  trailer: 'Przyczepa',
  'travel trailer': 'Przyczepa kempingowa',
  boat: 'Łódź',
  'industrial equipment': 'Sprzęt przemysłowy',
  other: 'Inny',
};

/**
 * Sale documents are the one field where the raw wording is the legal fact —
 * the states brand titles differently and buyers know "salvage" and "clean" by
 * name — so the reading is a GLOSS matched on patterns, and the raw name is
 * always returned with it. Order matters: the more specific brand wins.
 */
const DOCUMENT_PATTERNS: [RegExp, string][] = [
  [/DESTRUCTION/, 'Do kasacji — tylko na części'],
  [/PARTS ONLY/, 'Tylko na części'],
  [/MV-?907A/, 'MV-907A — szkoda całkowita (NY)'],
  [/REBUILDABLE|REBLD|RECONSTRUCTE/, 'Salvage do odbudowy'],
  [/REBUILT/, 'Rebuilt — odbudowany'],
  [/FLOOD/, 'Tytuł z wpisem o zalaniu'],
  [/REPOSSESSION/, 'Z windykacji'],
  [/BILL OF SALE/, 'Bill of sale — bez tytułu'],
  [/SALVAGE|SLVG/, 'Salvage — szkoda całkowita'],
  [/CLEAR|CLEAN/, 'Clean — czysty'],
  [/CERT(IFICATE)? OF (VEH(ICLE)? )?TITLE|ORIGINAL/, 'Tytuł własności'],
  [/VESSEL|WATERCRAFT/, 'Dokument jednostki pływającej'],
];

export const readDamage = (value: string | null): TReading | null => lookup(DAMAGE, value);
export const readRunCondition = (value: string | null): TReading | null => lookup(RUN_CONDITION, value);
export const readSellerType = (value: string | null): TReading | null => lookup(SELLER_TYPE, value);
export const readLossType = (value: string | null): TReading | null => lookup(LOSS_TYPE, value);
export const readColor = (value: string | null): TReading | null => lookup(COLOR, value);
export const readFuel = (value: string | null): TReading | null => lookup(FUEL, value);
export const readTransmission = (value: string | null): TReading | null => lookup(TRANSMISSION, value);
export const readDrive = (value: string | null): TReading | null => lookup(DRIVE, value);
export const readBodyStyle = (value: string | null): TReading | null => lookup(BODY_STYLE, value);
export const readVehicleType = (value: string | null): TReading | null => lookup(VEHICLE_TYPE, value);

export const readDocument = (value: string | null): TReading | null => {
  if (value === null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  const match = DOCUMENT_PATTERNS.find(([pattern]) => pattern.test(upper));
  return match ? { label: match[1], raw: trimmed } : { label: trimmed, raw: null };
};

/**
 * Whether the run condition is a positive finding — drives the icon, not the
 * wording. "Vehicle starts" is positive; "Enhanced" is a Copart programme
 * label, not a run report, so it stays neutral.
 */
export const isRunPositive = (value: string | null): boolean | null => {
  if (value === null) return null;
  const k = key(value);
  if (/^runs? (and|&) drive/.test(k) || k === 'vehicle starts' || k === 'engine start program') return true;
  if (k === 'stationary') return false;
  return null;
};
